// Performance optimization utilities
class PerformanceOptimizer {
  constructor() {
    this.cache = new Map();
    this.debounceTimers = new Map();
    this.throttleTimers = new Map();
  }

  // Debounce function calls
  debounce(func, delay, key) {
    return (...args) => {
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key));
      }
      
      const timer = setTimeout(() => {
        func.apply(this, args);
        this.debounceTimers.delete(key);
      }, delay);
      
      this.debounceTimers.set(key, timer);
    };
  }

  // Throttle function calls
  throttle(func, delay, key) {
    return (...args) => {
      if (this.throttleTimers.has(key)) {
        return;
      }
      
      func.apply(this, args);
      this.throttleTimers.set(key, true);
      
      setTimeout(() => {
        this.throttleTimers.delete(key);
      }, delay);
    };
  }

  // Simple cache implementation
  cacheResult(key, fn, ttl = 5 * 60 * 1000) { // 5 minutes default TTL
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }

    const result = fn();
    this.cache.set(key, {
      data: result,
      timestamp: Date.now()
    });

    return result;
  }

  // Clear cache
  clearCache(key) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Image optimization
  optimizeImage(file, maxWidth = 800, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const optimizedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(optimizedDataUrl);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  // Lazy load images
  lazyLoadImages() {
    const images = document.querySelectorAll('img[data-src]');
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  // Preload critical resources
  preloadResource(href, as = 'script') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    document.head.appendChild(link);
  }

  // Batch DOM updates
  batchDOMUpdates(updates) {
    requestAnimationFrame(() => {
      updates.forEach(update => update());
    });
  }

  // Memory cleanup
  cleanup() {
    this.cache.clear();
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.throttleTimers.clear();
  }
}

// Create global instance
const performanceOptimizer = new PerformanceOptimizer();

// Export utilities
export const debounce = (func, delay, key) => performanceOptimizer.debounce(func, delay, key);
export const throttle = (func, delay, key) => performanceOptimizer.throttle(func, delay, key);
export const cacheResult = (key, fn, ttl) => performanceOptimizer.cacheResult(key, fn, ttl);
export const clearCache = (key) => performanceOptimizer.clearCache(key);
export const optimizeImage = (file, maxWidth, quality) => performanceOptimizer.optimizeImage(file, maxWidth, quality);
export const lazyLoadImages = () => performanceOptimizer.lazyLoadImages();
export const preloadResource = (href, as) => performanceOptimizer.preloadResource(href, as);
export const batchDOMUpdates = (updates) => performanceOptimizer.batchDOMUpdates(updates);

export default performanceOptimizer;


